import { Box, IconButton, SvgIcon, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel,
  Button, FormControl, Grid, TextField, Select, MenuItem, InputLabel, Chip, Popover } from '@mui/material';
import { Trash01, SearchSm } from '../../../../assets';
import { theme } from '../../../../theme';
import React, { useState } from 'react';
import CustomPagination from '../../../../components/forms/Pagination';
import { usePosMappingsContext } from '../../context/PosMappingsContext';
import { PaperPackingType } from '../../enums/PaperPackagingType';
import { UpdateStoreName } from '../../../../app/services/api/generated';
import { EmptyTable } from '../../../../components/shared/EmptyTable';

export function PaperPackagingRelations() {
  const {
    t,
    skuSearch,
    storeSearch,
    paperPackagingType,
    handleSkuSearchChange,
    handleStoreSearchChange,
    handlePaperPackagingTypeChange,
    clearAll,
    searchPOSMapping,
    totalCount,
    page,
    setPage,
    maxPages,
    paperPackagingMappings,
    handleSort,
    order,
    orderBy,
    posMappingDelete,
    edit,
    openCreateModal,
  } = usePosMappingsContext();

  const [sizeAnchorEl, setSizeAnchorEl] = useState<null | HTMLElement>(null);
  const [popoverSizes, setPopoverSizes] = useState<string[]>([]);
  const [storeAnchorEl, setStoreAnchorEl] = useState<null | HTMLElement>(null);
  const [popoverStores, setPopoverStores] = useState<UpdateStoreName[]>([]);

  const handleOpenSizePopover = (event: React.MouseEvent<HTMLElement>, extraSizes: string[]) => {
    event.stopPropagation();
    setSizeAnchorEl(event.currentTarget);
    setPopoverSizes(extraSizes);
  };

  const handleCloseSizePopover = () => {
    setSizeAnchorEl(null);
    setPopoverSizes([]);
  };

  const handleOpenStorePopover = (event: React.MouseEvent<HTMLElement>, extraStores: UpdateStoreName[]) => {
    event.stopPropagation();
    setStoreAnchorEl(event.currentTarget);
    setPopoverStores(extraStores);
  };

  const handleCloseStorePopover = () => {
    setStoreAnchorEl(null);
    setPopoverStores([]);
  };

  const isSizePopoverOpen = Boolean(sizeAnchorEl);
  const isStorePopoverOpen = Boolean(storeAnchorEl);

  const getChipStyle = (size: string) => {
    switch (size) {
    case '4 oz':
      return { backgroundColor: '#d1f7e2', color: '#007f5f' };
    case '6 oz':
      return { backgroundColor: '#d0ebff', color: '#1c7ed6' };
    case '8 oz':
      return { backgroundColor: '#e5dbff', color: '#5f3dc4' };
    case '10 oz':
      return { backgroundColor: '#ffe066', color: '#795548' };
    case '12 oz':
      return { backgroundColor: '#ffc9de', color: '#c2185b' };
    case '16 oz':
      return { backgroundColor: '#ffccbc', color: '#d84315' };
    default:
      return { backgroundColor: '#e0f7fa', color: '#007c91' };
    }
  };

  const getPaperPackagingTypeLabel = (type: number) => {
    switch (type) {
    case PaperPackingType.HotCup:
      return 'Hot Cup';
    case PaperPackingType.HotLid:
      return 'Hot Lid';
    case PaperPackingType.ColdCup:
      return 'Cold Cup';
    case PaperPackingType.ColdFlatLid:
      return 'Cold Flat Lid';
    case PaperPackingType.ColdDomedLid:
      return 'Cold Domed Lid';
    case PaperPackingType.Sleeve:
      return 'Sleeve';
    case PaperPackingType.Straw:
      return 'Straw';
    case PaperPackingType.NitroLid:
      return 'Nitro Lid';
    default:
      return type?.toString() || '--';
    }
  };

  const paperPackagingTypeValues = Object.values(PaperPackingType).filter(v => typeof v === 'number');

  return (
    <Box sx={{ mt: 1 }}>
      <Box
        sx={{
          border: '1px solid',
          borderColor: theme.palette.custom.gray[300],
          py: '0px',
          textAlign: 'left',
        }}
      >
        <Grid
          container
          spacing={5}
          sx={{
            alignItems: 'flex-end',
            borderColor: theme.palette.custom.gray[300],
            px: '8px',
            py: '12px',
          }}
        >
          <Grid item
            xs={12}
            md={3}>
            <FormControl fullWidth
              size="small">
              <TextField
                size="small"
                id="item-number-search"
                label={t('skuSearchPpm')}
                variant="outlined"
                value={skuSearch}
                onChange={handleSkuSearchChange}
              />
            </FormControl>
          </Grid>

          <Grid item
            xs={12}
            md={3}>
            <FormControl fullWidth
              size="small">
              <TextField
                size="small"
                id="store-search"
                label={t('storeSearch')}
                variant="outlined"
                value={storeSearch}
                onChange={handleStoreSearchChange}
              />
            </FormControl>
          </Grid>

          <Grid item
            xs={12}
            md={3}>
            <FormControl fullWidth
              size="small">
              <InputLabel id="paper-packaging-type-label">{t('paperPackagingType')}</InputLabel>
              <Select
                labelId="paper-packaging-type-label"
                id="paper-packaging-type-select"
                value={paperPackagingType ?? ''}
                label={t('paperPackagingType')}
                onChange={(e) => handlePaperPackagingTypeChange(e.target.value === '' ? undefined : Number(e.target.value))}
              >
                {paperPackagingTypeValues.map((value) => (
                  <MenuItem key={value}
                    value={value}>
                    {getPaperPackagingTypeLabel(value as number)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item
            xs={12}
            md={1.5}>
            <Button variant="secondary"
              size="lg"
              onClick={clearAll}
              fullWidth>
              {t('clearAll')}
            </Button>
          </Grid>

          <Grid item
            xs={12}
            md={1.5}>
            <Button
              variant="primary"
              size="lg"
              onClick={searchPOSMapping}
              startIcon={<SearchSm />}
              fullWidth
            >
              {t('search')}
            </Button>
          </Grid>
        </Grid>
      </Box>

      {totalCount === 0 ? (
        <EmptyTable
          text={t('paperPackagingMapping')}
          searchTerm={skuSearch}
          onClear={clearAll}
          onAdd={openCreateModal}
          onAddIsCreating={false}
          onAddIsDisabled={false}
          addItemText={t('createNew')}
        />
      ) : (
        <>
          <TableContainer component="div"
            sx={{ mt: 2 }}>
            <Table
              size="small"
              aria-label={t('paperPackagingMappingTable')}
            >
              <TableHead>
                <TableRow>
                  <TableCell align="left">
                    <TableSortLabel
                      active={orderBy === 'itemNumber'}
                      direction={order}
                      onClick={() => handleSort('itemNumber')}
                    >
                      {t('imSku')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="left">
                    <TableSortLabel
                      active={orderBy === 'itemDescription'}
                      direction={order}
                      onClick={() => handleSort('itemDescription')}
                    >
                      {t('itemDesc')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="left">
                    <TableSortLabel
                      active={orderBy === 'cupsLidsType'}
                      direction={order}
                      onClick={() => handleSort('cupsLidsType')}
                    >
                      {t('paperPackagingType')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ minWidth: 200 }}
                  >
                    {t('cupSize')}
                  </TableCell>
                  <TableCell align="left">
                    <TableSortLabel
                      active={orderBy === 'storeName'}
                      direction={order}
                      onClick={() => handleSort('storeName')}
                    >
                      {t('stores')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="left"></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paperPackagingMappings?.map((row, index) => (
                  <TableRow key={index}
                    hover
                    sx={{
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: '#d1f7e2',
                        '& td': {
                          color: '#007f5f',
                          fontWeight: 500,
                        },
                      },
                    }}
                    onClick={() => edit(row)}
                  >
                    <TableCell>{row.itemNumber}</TableCell>
                    <TableCell>{row.itemDescription}</TableCell>
                    <TableCell>{getPaperPackagingTypeLabel(row.paperPackagingType ?? 0)}</TableCell>
                    <TableCell
                      sx={{ minWidth: 200 }}
                    >
                      <TableCell>
                        {row.sizes && row.sizes.length > 0 ? (() => {
                          const sizeArray = row.sizes.map((s: number) => `${s} oz`);
                          const maxVisible = 3;
                          const visibleSizes = sizeArray.slice(0, maxVisible);
                          const extraSizes = sizeArray.slice(maxVisible);

                          return (
                            <>
                              {visibleSizes.map((size, idx) => (
                                <Chip
                                  key={idx}
                                  label={size}
                                  sx={{
                                    m: 0.5,
                                    fontWeight: 500,
                                    ...getChipStyle(size),
                                  }}
                                />
                              ))}
                              {extraSizes.length > 0 && (
                                <Chip
                                  label={`+${extraSizes.length}`}
                                  clickable
                                  onClick={(e) => handleOpenSizePopover(e, extraSizes)}
                                  sx={{
                                    m: 0.5,
                                    backgroundColor: '#f0f0f0',
                                    fontWeight: 500,
                                    color: '#333',
                                    cursor: 'pointer',
                                    '&:hover': { backgroundColor: '#e0e0e0' },
                                  }}
                                />
                              )}
                            </>
                          );
                        })() :''}
                      </TableCell>
                    </TableCell>
                    <TableCell>
                      {Array.isArray(row.stores) && (row.stores?.length ?? 0) > 0 ? (
                        <Box display="flex"
                          flexDirection="column"
                          alignItems="flex-start"
                          gap={0.5}>
                          <Box display="flex"
                            flexWrap="wrap"
                            gap={1}>
                            {row.stores!.slice(0, 3).map((store, storeIdx) => (
                              <Chip
                                key={storeIdx}
                                label={store.storeName ?? ''}
                                size="small"
                                sx={{ m: 0.5 }}
                              />
                            ))}
                          </Box>
                          {row.stores && row.stores.length > 3 && (
                            <Chip
                              label={`+${row.stores.length - 3}`}
                              size="small"
                              clickable
                              onClick={(e) => handleOpenStorePopover(e, row.stores?.slice(3) || [])}
                              sx={{
                                backgroundColor: theme.palette.custom.gray[200],
                                cursor: 'pointer',
                                '&:hover': { backgroundColor: theme.palette.custom.gray[300] },
                              }}
                            />
                          )}
                        </Box>
                      ) : (
                        <Chip label={t('allStores')}
                          size="small"
                          color="default" />
                      )}
                    </TableCell>
                    <TableCell align="left">
                      <Box display="flex"
                        gap={1}>
                        <Box
                          onClick={(e) => e.stopPropagation()}
                          sx={{ '&:hover': { backgroundColor: 'transparent !important' } }}
                        >
                          <IconButton size="small"
                            onClick={() => posMappingDelete(row)}>
                            <SvgIcon style={{ color: theme.palette.error[500] }}>
                              <Trash01 />
                            </SvgIcon>
                          </IconButton>
                        </Box>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <CustomPagination
            page={page}
            setPage={setPage}
            maxPages={maxPages}
            breakpointForChangeDisplay={120000}
          />
          <Popover
            open={isSizePopoverOpen}
            anchorEl={sizeAnchorEl}
            onClose={handleCloseSizePopover}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          >
            <Box display="flex"
              gap={1}
              flexWrap="wrap"
              maxWidth={300}
              p={1}>
              {popoverSizes.map((size, idx) => (
                <Chip
                  key={idx}
                  label={size}
                  sx={{
                    fontWeight: 500,
                    ...getChipStyle(size),
                  }}
                />
              ))}
            </Box>
          </Popover>
          <Popover
            open={isStorePopoverOpen}
            anchorEl={storeAnchorEl}
            onClose={handleCloseStorePopover}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          >
            <Box
              display="flex"
              gap={1}
              flexWrap="wrap"
              maxWidth={500}
              minWidth={300}
              p={2}
            >
              {popoverStores.map((store, idx) => (
                <Chip
                  key={idx}
                  label={store.storeName ?? ''}
                  size="small"
                  sx={{ m: 0.5 }}
                />
              ))}
            </Box>
          </Popover>
        </>
      )}
    </Box>
  );
}
